// ATTICUS-ANALYTICS-COMMAND-CENTER-1 — Financial / CFO lens.
//
// Reserve composition, paid-by-category (10 real coded categories) with a
// PROPOSED indemnity/LAE rollup (clearly labeled, not faked), severity by
// dimension, reserve aging, funds outstanding, and honest deferred cards for
// subrogation / operating margin / IBNR.
import React, { useEffect, useState, useMemo } from "react";
import { apiFetch, fmt } from "../../api";
import { KpiCard, ChartCard, MetricUnavailableState, buildQS } from "../AnalyticsKit";
import { Donut, HBar, GroupedBar, PALETTE } from "../charts";

export default function FinancialLens({ filters, openDrill, reloadKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    apiFetch(`/analytics/financial?${buildQS(filters)}`)
      .then((d) => { if (live) { setData(d); setLoading(false); } })
      .catch((e) => { if (live) { setError(e.message || String(e)); setLoading(false); } });
    return () => { live = false; };
  }, [filters, reloadKey]);

  const m = (v) => fmt.money(v);
  const rc = data?.reserve_composition || {};
  const paidPct = rc.total_reserve ? Math.round((rc.total_paid / rc.total_reserve) * 100) : 0;

  const categories = useMemo(() => (data?.paid_by_category || [])
    .filter((c) => c.amount > 0)
    .map((c, i) => ({ name: c.label, value: c.amount, color: PALETTE[i % PALETTE.length] })), [data]);

  const rollup = data?.category_rollup_proposed;
  const rollupData = rollup ? [
    { name: "Indemnity", value: rollup.indemnity, color: "#3b6fd4" },
    { name: "LAE / Expense", value: rollup.lae, color: "#f59e0b" },
    { name: "Reimbursements", value: rollup.reimbursements, color: "#14b8a6" },
  ].filter((d) => d.value > 0) : [];

  const blData = useMemo(() => (data?.avg_cost_by_business_line || [])
    .map((r) => ({ name: r.name, value: r.avg_paid, count: r.count })), [data]);
  const ctData = useMemo(() => (data?.avg_cost_by_claim_type || [])
    .map((r) => ({ name: r.name, value: r.avg_paid, count: r.count })), [data]);
  const ageData = useMemo(() => (data?.reserve_by_age || [])
    .map((r) => ({ name: r.bucket, value: r.remaining, count: r.count })), [data]);
  const fundsData = useMemo(() => (data?.funds_outstanding_by_insured || [])
    .map((r) => ({ name: r.name, value: r.amount, _cid: r.company_id })), [data]);

  if (error) return <div className="an-error">Couldn't load financial analytics: {error}</div>;
  const def = data?.deferred || {};

  return (
    <div className="an-stack">
      {/* Reserve composition */}
      <div className="an-kpis" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
        <KpiCard label="Total Reserve" tone="navy" value={loading ? "…" : m(rc.total_reserve)} sub="open claims" />
        <KpiCard label="Total Paid" tone="blue" value={loading ? "…" : m(rc.total_paid)} sub={`${paidPct}% of reserve`} />
        <KpiCard label="Remaining Reserve" tone="green" value={loading ? "…" : m(rc.remaining)} sub={`${100 - paidPct}% of reserve`} />
      </div>

      <div className="an-grid">
        <ChartCard title="Paid by Category" subtitle="coded payment categories (in period)" wide
                   loading={loading && !data} empty={!categories.length}>
          <Donut data={categories} money />
        </ChartCard>

        <ChartCard title="Indemnity vs LAE" subtitle="paid composition (in period)"
                   controls={<span className="pill pill-amber pill-sm" title="Bucketing of the 10 coded categories is a proposed mapping pending confirmation — the underlying category totals are exact.">Proposed mapping</span>}
                   loading={loading && !data} empty={!rollupData.length}>
          <HBar data={rollupData} money />
        </ChartCard>

        <ChartCard title="Avg Cost per Claim — Business Line" subtitle="lifetime paid ÷ claims; click to drill"
                   loading={loading && !data} empty={!blData.length}>
          <HBar data={blData} money onSegmentClick={(p) => openDrill("business_line", p.name, `${p.name} claims`)} color="#3b6fd4" />
        </ChartCard>

        <ChartCard title="Avg Cost per Claim — Claim Type" subtitle="lifetime paid ÷ claims"
                   loading={loading && !data} empty={!ctData.length}>
          <HBar data={ctData} money color="#6366f1" />
        </ChartCard>

        <ChartCard title="Outstanding Reserve by Age" subtitle="open claims by age; click a bucket"
                   loading={loading && !data} empty={!ageData.some((d) => d.value > 0)}>
          <HBar data={ageData} money onSegmentClick={(p) => openDrill("reserve_age_bucket", p.name, `Open claims aged ${p.name} days`)} color="#0ea5e9" />
        </ChartCard>

        <ChartCard title="Funds Outstanding by Payor" subtitle="sent funds requests"
                   loading={loading && !data} empty={!fundsData.length}
                   unavailable={!loading && !fundsData.length ? { status: "Partial", reason: "No funds requests are currently outstanding (Sent). This populates as requests are sent." } : undefined}>
          <HBar data={fundsData} money onSegmentClick={(p) => p._cid && openDrill("insured", String(p._cid), `${p.name} claims`)} color="#14b8a6" />
        </ChartCard>

        <ChartCard title="Subrogation Recovery Rate"
                   unavailable={{ status: "Deferred", reason: def.subrogation_recovery, dependency: "native recovery subsystem" }} />
        <ChartCard title="Operating Margin per Claim"
                   unavailable={{ status: "Deferred", reason: def.operating_margin, dependency: "service-revenue + handling-cost model" }} />
        <ChartCard title="IBNR Estimate"
                   unavailable={{ status: "Deferred", reason: def.ibnr, dependency: "approved actuarial source" }} />
      </div>
    </div>
  );
}
